import Phaser from "phaser";

const MAX_RADIUS = 70;
const DEAD_ZONE = 6;

/**
 * Floating one-thumb virtual joystick. Touch (or click) anywhere to anchor the
 * base; drag to steer. Returns a direction vector with magnitude in [0..1].
 */
export default class Joystick {
  private base: Phaser.GameObjects.Sprite;
  private thumb: Phaser.GameObjects.Sprite;
  private pointerId: number | null = null;
  private vec = new Phaser.Math.Vector2(0, 0);

  constructor(scene: Phaser.Scene) {
    this.base = scene.add
      .sprite(0, 0, "joystick-base")
      .setScrollFactor(0)
      .setDepth(100)
      .setVisible(false);
    this.thumb = scene.add
      .sprite(0, 0, "joystick-thumb")
      .setScrollFactor(0)
      .setDepth(101)
      .setVisible(false);

    scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onUp, this);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy(scene));
  }

  private onDown(pointer: Phaser.Input.Pointer): void {
    if (this.pointerId !== null) return;
    this.pointerId = pointer.id;
    this.base.setPosition(pointer.x, pointer.y).setVisible(true);
    this.thumb.setPosition(pointer.x, pointer.y).setVisible(true);
  }

  private onMove(pointer: Phaser.Input.Pointer): void {
    if (pointer.id !== this.pointerId) return;
    const dx = pointer.x - this.base.x;
    const dy = pointer.y - this.base.y;
    const dist = Math.hypot(dx, dy);
    const clamped = Math.min(dist, MAX_RADIUS);
    const angle = Math.atan2(dy, dx);
    this.thumb.setPosition(
      this.base.x + Math.cos(angle) * clamped,
      this.base.y + Math.sin(angle) * clamped,
    );
    if (dist < DEAD_ZONE) {
      this.vec.set(0, 0);
    } else {
      const mag = clamped / MAX_RADIUS;
      this.vec.set(Math.cos(angle) * mag, Math.sin(angle) * mag);
    }
  }

  private onUp(pointer: Phaser.Input.Pointer): void {
    if (pointer.id !== this.pointerId) return;
    this.pointerId = null;
    this.vec.set(0, 0);
    this.base.setVisible(false);
    this.thumb.setVisible(false);
  }

  /** Current steering vector, magnitude 0..1. */
  get vector(): Phaser.Math.Vector2 {
    return this.vec;
  }

  private destroy(scene: Phaser.Scene): void {
    scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    scene.input.off(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onUp, this);
    this.base.destroy();
    this.thumb.destroy();
  }
}
